import styled from 'styled-components';
import React, {useEffect} from 'react';
import {useComponent} from '../../component-library';
import {Thumb} from './thumb';
import {useRefState} from '../../utils/use-ref-state';
import {SliderConstants} from './constants';
import {useFeatureAppId} from '../../utils/use-feature-app-id';
import {generateTestId, TestIdElementType} from '../../utils/test';
import {UserDrivingProfile} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';

interface AreaConfig {
  name: string;
  icon: React.ReactNode;
  initialValue: number;
}

export type AreaConfigs = [AreaConfig, AreaConfig, AreaConfig];
export interface ThreeAreasPercentageSliderProps {
  onChangeImmediate: (value: [number, number, number]) => void;
  onChange?: (value: [number, number, number]) => void;
  /* id to be referenced by aria-labelledby */
  labelId: string;
  id: string;
  areas: AreaConfigs;
  selectedUserDrivingProfile?: UserDrivingProfile;
}

const clampAndRound = (min: number, num: number, max: number): number => {
  let value = Math.round(num);
  value = Math.min(max, value);
  value = Math.max(min, value);

  return value;
};

const getAreaPercentages = ([sep1Percentage, sep2Percentage]: [
  number,
  number
]) =>
  [sep1Percentage, sep2Percentage - sep1Percentage, 100 - sep2Percentage] as [
    number,
    number,
    number
  ];

export const ThreeAreasPercentageSlider = React.memo(
  ({
    onChangeImmediate,
    labelId,
    id,
    areas: [area1, area2, area3],
    onChange,
    selectedUserDrivingProfile,
  }: ThreeAreasPercentageSliderProps): JSX.Element => {
    const railElement = React.useRef<HTMLDivElement>(null);
    const featureAppId = useFeatureAppId();

    const [stateValues, setStateValues, stateValuesRef] = useRefState<
      [number, number]
    >([area1.initialValue, area1.initialValue + area2.initialValue]);

    const setStateValuesImmediate = React.useCallback(
      (newState: [number, number]) => {
        onChangeImmediate(getAreaPercentages(newState));
        setStateValues(newState);
      },
      [onChangeImmediate, setStateValues]
    );

    const [slidingState, setSlidingState, slidingStateRef] = useRefState<
      [boolean, boolean]
    >([false, false]);

    const slideChangeHandlers = React.useMemo(
      () =>
        [0, 1].map((index) => (isSliding: boolean) => {
          const newState = [...slidingStateRef.current] as [boolean, boolean];
          newState[index] = isSliding;
          setSlidingState(newState);

          // onChange needs to be triggered when sliding stops
          if (!isSliding) {
            onChange?.(getAreaPercentages(stateValuesRef.current));
          }
        }),
      [setSlidingState, slidingStateRef, stateValuesRef, onChange]
    );

    const valueChangeHandlers = React.useMemo(
      () => [
        (update: (old: number) => number) => {
          // thumb 1
          const newValue = clampAndRound(
            SliderConstants.MIN,
            update(stateValuesRef.current[0]),
            stateValuesRef.current[1]
          );

          if (newValue !== stateValuesRef.current[0]) {
            setStateValuesImmediate([newValue, stateValuesRef.current[1]]);
          }
        },
        (update: (old: number) => number) => {
          // thumb 2
          const newValue = clampAndRound(
            stateValuesRef.current[0],
            update(stateValuesRef.current[1]),
            SliderConstants.MAX
          );

          if (newValue !== stateValuesRef.current[1]) {
            setStateValuesImmediate([stateValuesRef.current[0], newValue]);
          }
        },
      ],
      [setStateValuesImmediate, stateValuesRef]
    );

    const [area1Percentage, area2Percentage, area3Percentage] =
      getAreaPercentages(stateValues);

    const valuesId = `${id}-values`;
    const Text = useComponent('Text');

    useEffect(() => {
      if (selectedUserDrivingProfile) {
        const {city, country} = selectedUserDrivingProfile.drivingLocation;
        setStateValuesImmediate([city, stateValuesRef.current[1]]);
        setStateValuesImmediate([stateValuesRef.current[0], city + country]);
      }
    }, [selectedUserDrivingProfile]);

    return (
      <>
        <RailBorder>
          <Rail ref={railElement}>
            {/* Highly dynamic / animated props should be set using inline styles */}
            <RailArea percentage={area1Percentage} isSliding={slidingState[0]}>
              {area1.icon}
            </RailArea>
            <Separator>
              <Thumb
                value={stateValues[0]}
                thumbMin={SliderConstants.MIN}
                thumbMax={stateValues[1]}
                ariaValueText={`${area1Percentage}% ${area1.name} ${area2Percentage}% ${area2.name}`}
                ariaLabelledBy={labelId}
                ariaDescribedBy={valuesId}
                position="top"
                testId={generateTestId({
                  featureAppId,
                  elementName: 'section-driving-locations-slider-left',
                  elementType: TestIdElementType.handle,
                })}
                railElementRef={railElement}
                onSlidingChange={slideChangeHandlers[0]}
                onValueChange={valueChangeHandlers[0]}
              />
            </Separator>
            <RailArea
              odd
              percentage={area2Percentage}
              isSliding={slidingState[0] || slidingState[1]}
            >
              {area2.icon}
            </RailArea>
            <Separator>
              <Thumb
                value={stateValues[1]}
                thumbMin={stateValues[0]}
                thumbMax={SliderConstants.MAX}
                ariaValueText={`${area2Percentage}% ${area2.name} ${area3Percentage}% ${area3.name}`}
                ariaLabelledBy={labelId}
                ariaDescribedBy={valuesId}
                position="bottom"
                testId={generateTestId({
                  featureAppId,
                  elementName: 'section-driving-locations-slider-right',
                  elementType: TestIdElementType.handle,
                })}
                railElementRef={railElement}
                onSlidingChange={slideChangeHandlers[1]}
                onValueChange={valueChangeHandlers[1]}
              />
            </Separator>
            <RailArea percentage={area3Percentage} isSliding={slidingState[1]}>
              {area3.icon}
            </RailArea>
          </Rail>
        </RailBorder>
        <Values id={valuesId}>
          <Value>
            <Text
              variant="copy"
              tag="span"
              bold
              testid={generateTestId({
                featureAppId,
                elementName: 'section-city-percentage',
                elementType: TestIdElementType.input,
              })}
            >
              {area1Percentage}%
            </Text>
            <Text variant="copy" tag="span">
              {area1.name}
            </Text>
          </Value>
          <Value>
            <Text
              variant="copy"
              tag="span"
              bold
              testid={generateTestId({
                featureAppId,
                elementName: 'section-countryroad-percentage',
                elementType: TestIdElementType.input,
              })}
            >
              {area2Percentage}%
            </Text>
            <Text variant="copy" tag="span">
              {area2.name}
            </Text>
          </Value>
          <Value>
            <Text
              variant="copy"
              tag="span"
              bold
              testid={generateTestId({
                featureAppId,
                elementName: 'section-highway-percentage',
                elementType: TestIdElementType.input,
              })}
            >
              {area3Percentage}%
            </Text>
            <Text variant="copy" tag="span">
              {area3.name}
            </Text>
          </Value>
        </Values>
      </>
    );
  }
);

ThreeAreasPercentageSlider.displayName = 'ThreeAreasPercentageSlider';

const RailBorder = styled.div`
  display: flex;
  height: 80px;

  /* half a thumb */
  margin: ${SliderConstants.THUMB_SIZE / 2} 0;

  /* add extra space to the sides so user doesn't accidentally navigate on mobile */
  padding: 0 ${(props) => props.theme.internal.size.grid001};
  border: ${(props) => props.theme.internal.size.border.light} solid
    ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.separator};
  border-radius: ${(props) => props.theme.brand.elements.borderRadius};

  @media (min-width: ${(props) => props.theme.internal.Breakpoints.b960}px) {
    padding: 0;
  }
`;

const Rail = styled.div`
  display: flex;
  position: relative;
  flex: 1;
  flex-direction: row;

  /* enables us to use z-index inside without affecting anything outside */
  isolation: isolate;
  user-select: none;
`;

interface RailAreaProps {
  odd?: boolean;
  percentage: number;
  isSliding: boolean;
}
const RailArea = styled.div.attrs<RailAreaProps>((props) => ({
  style: {
    flexGrow: props.percentage,
    willChange: props.isSliding ? 'flex-grow' : undefined,
  },
  odd: props.odd,
}))<RailAreaProps>`
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
  background: ${(props) =>
    props.odd
      ? props.theme.brand.colors.inputArea.drivingProfileSlider.oddBackground
      : props.theme.brand.colors.inputArea.drivingProfileSlider.evenBackground};
  color: ${(props) =>
    props.theme.brand.colors.inputArea.drivingProfileSlider.text ?? 'inherit'};
`;

const Separator = styled.div`
  position: relative;
  width: 0;
  height: 100%;
  border-left: ${(props) => props.theme.internal.size.border.light} dashed
    ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.separator};

  /*
This means dragging will not be cancelled by e.g. the browser suddenly
thinking you wanted to scroll up or down instead. (we restrict to pinch-zoom,
and exclude panning gestures used for scrolling)

We need to be careful to not apply it to a big  area, so we are not annoying
users with a big area where scrolling won't work. As far as I can tell, it is
only important that the Thumb's parent has this set, not how big that parent
actually is.
   */
  touch-action: pinch-zoom;
`;

const Values = styled.div`
  display: flex;
  margin-top: ${(props) => props.theme.internal.size.static350};
`;

const Value = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  align-items: center;
`;
